import type {
  PageViewEvent,
  StructuredEvent,
  TrackerConfiguration,
} from "@snowplow/browser-tracker";
import {
  newTracker,
  setCookiePath,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
  trackStructEvent,
} from "@snowplow/browser-tracker";
import { GaCookiesPlugin } from "@snowplow/browser-plugin-ga-cookies";
import { makeContexts, updateIdentityContext } from "./contexts";
import type { EventDefinition, SerialisedEvent, TrackingProps } from "./types";

export type FrontOfficeStructuredEvent = StructuredEvent & {
  serviceChannelIdentifier: string;
};

/**
 * This class is an abstraction which wraps Snowplow
 * and exposes common methods with other services:
 * - trackEvent            : sends a standard payload
 * - trackUnstructEvent    : sends a payload for custom schema
 */
export class Snowplow {
  avalancheTrackerName = "sb-ava";

  bronzeAvalancheTrackerName = "sb-ava-br";

  pvAvalancheTrackerName = "sb-ava-pv";

  uid: unknown = "";

  trackPageView: boolean = false;

  contexts: Record<string, SerialisedEvent> = {};

  pvContext: SerialisedEvent[] = [];

  structContext: SerialisedEvent[] = [];

  serverData: Record<string, unknown> = {};

  eventHandlers: Record<string, (params?: Record<string, unknown>) => void> =
    {};

  static instance: Snowplow | undefined;

  constructor(props?: TrackingProps) {
    if (!props) return;

    const {
      appId,
      cookieDomain,
      avalancheCollector,
      eventMethod,
      uid,
      postPath,
      trackPageView: tpv,
    } = props;
    this.uid = uid;
    this.trackPageView = tpv;
    this.serverData = props?.pageViewContext?.data || {};

    // Set options
    const stateStorageStrategy = "cookieAndLocalStorage";
    const baseOptions: TrackerConfiguration = {
      appId,
      cookieDomain,
      eventMethod,
      stateStorageStrategy,
      postPath,
    };
    // Initialize trackers
    newTracker(this.avalancheTrackerName, avalancheCollector, baseOptions);

    newTracker(
      this.bronzeAvalancheTrackerName,
      avalancheCollector,
      baseOptions,
    );

    // Page view tracker
    newTracker(this.pvAvalancheTrackerName, avalancheCollector, {
      ...baseOptions,
      eventMethod: eventMethod === "post" ? "beacon" : eventMethod,
      plugins: [GaCookiesPlugin()],
    });

    setCookiePath("/");
    if (uid) {
      setUserId(uid);
    }
    // Create a singleton instance
    Snowplow.instance = this;
  }

  setContexts(contexts: Record<string, SerialisedEvent>) {
    this.contexts = updateIdentityContext(contexts, this.uid);
    this.pvContext = makeContexts(
      [
        "pageViewContext",
        "distributionChannelContext",
        "serviceChannelContext",
      ],
      this.contexts,
    );
    this.structContext = makeContexts(
      ["distributionChannelContext", "serviceChannelContext"],
      this.contexts,
    );
    return this;
  }

  // Send a page view event
  trackView(event?: PageViewEvent) {
    if (this.trackPageView) {
      trackPageView({ ...event, context: this.pvContext }, [
        this.pvAvalancheTrackerName,
      ]);
    }
    return this;
  }

  // Send a structured event with contexts
  trackEvent(event: StructuredEvent) {
    trackStructEvent({ ...event, context: this.structContext }, [
      this.bronzeAvalancheTrackerName,
    ]);
    return this;
  }

  // Send a custom event with defined schema and optional contexts
  trackUnstructEvent(event: SerialisedEvent, contexts?: string[]) {
    if (!event) {
      return this;
    }

    if (contexts && Array.isArray(contexts) && contexts.length > 0) {
      // Add context to the event
      const context = makeContexts(contexts, this.contexts);
      trackSelfDescribingEvent({ event, context }, [this.avalancheTrackerName]);
    } else {
      trackSelfDescribingEvent({ event }, [this.avalancheTrackerName]);
    }
    return this;
  }

  addEventHandlers(eventDefinitions: EventDefinition[]) {
    // Add server context to makePayload functions
    const context = this.serverData;

    eventDefinitions.forEach(({ name, type, makePayload, contexts }) => {
      // Convert type into relevant function
      if (type === "structured") {
        this.addEventHandler(name, (params?: Record<string, unknown>) => {
          void this.trackEvent(
            makePayload({
              ...params,
              context,
            }) as StructuredEvent,
          );
        });
      } else {
        this.addEventHandler(name, (params?: Record<string, unknown>) => {
          const payload = makePayload({
            ...params,
            context,
          }) as SerialisedEvent;

          if (contexts && Array.isArray(contexts) && contexts.length > 0) {
            void this.trackUnstructEvent(payload, contexts);
          } else {
            void this.trackUnstructEvent(payload);
          }
        });
      }
    });
    return this;
  }

  private addEventHandler(
    name: string,
    handler: (params?: Record<string, unknown>) => void,
  ) {
    this.eventHandlers[name] = handler;
    return this;
  }

  private removeEventHandler(name: string) {
    delete this.eventHandlers[name];
    return this;
  }

  trigger(name: string, params?: Record<string, unknown>) {
    if (!this.serverData || Object.keys(this.serverData).length === 0) {
      return this;
    }

    const handler = this.eventHandlers[name];

    if (
      Object.prototype.hasOwnProperty.call(this.eventHandlers, name) &&
      typeof handler === "function"
    ) {
      handler(params);
    }
    return this;
  }

  static getInstance(props?: TrackingProps) {
    if (!Snowplow.instance) {
      Snowplow.instance = new Snowplow(props);
    }
    return Snowplow.instance;
  }
}
